import { Button, Menu, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconChevronDown,
  IconDownload,
  IconFileSpreadsheet,
  IconFileTypePdf,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  ApiClientError,
  downloadExport,
  type ExportDataset,
  type ExportFormat,
} from "../../lib/api";

/**
 * Export, with the format chosen at the click.
 *
 * WHAT CHANGED AND WHY
 *
 * This was one button that wrote a CSV of the rows the screen was holding,
 * labelled "Export 47 rows". Both halves of that were wrong.
 *
 * The COUNT was wrong because it was the count of a capped list: every list
 * endpoint returns at most 200 rows (500 for stock and users), so a workshop
 * with 900 jobs exported 200 of them into a file that looked complete. The
 * label is now just "Export": the file covers the whole list, which is not a
 * number this screen knows, and the count that does matter is printed inside
 * the file along with the moment it was taken.
 *
 * The FORMAT was wrong because a CSV is not what anybody here wanted. Excel
 * needs a BOM to read one without mangling every rupee sign, a CSV carries no
 * column widths and no frozen header, and it cannot be handed to somebody to
 * read. Two formats, asked for explicitly: .xlsx to work in and .pdf to send.
 *
 * WHY THE BUTTON DISABLES ITSELF WHILE IT WORKS
 *
 * An export of a few thousand rows takes a moment on the server, and the
 * button gives no sign of it. Somebody who clicks twice gets two files, which
 * is confusing rather than harmful - but they also have no way to tell
 * whether the first click registered, which is how the second one happens.
 */
export function ExportMenu({
  dataset,
  /** Disabled when there is demonstrably nothing to export. */
  disabled,
}: {
  dataset: ExportDataset;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const run = async (format: ExportFormat) => {
    setBusy(format);
    try {
      await downloadExport(dataset, format);
    } catch (error) {
      notifications.show({
        color: "red",
        title: "The export did not download",
        message:
          error instanceof ApiClientError
            ? error.message
            : "Something went wrong building the file.",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Menu position="bottom-end" withinPortal shadow="md" width={260}>
      <Menu.Target>
        <Button
          variant="default"
          size="sm"
          disabled={disabled}
          loading={busy !== null}
          leftSection={<IconDownload size={16} stroke={1.8} />}
          rightSection={<IconChevronDown size={14} stroke={2} />}
        >
          Export
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {/* Named as the thing you get, with the extension, because that is
            what somebody looking for it in a Downloads folder will see. */}
        <Menu.Label>The whole list, not just this screen</Menu.Label>
        <Menu.Item
          leftSection={<IconFileSpreadsheet size={18} stroke={1.6} />}
          onClick={() => void run("xlsx")}
        >
          <Text fz="sm" fw={500}>
            Excel (.xlsx)
          </Text>
          <Text fz={11} c="gray.6">
            Every column, figures as numbers
          </Text>
        </Menu.Item>
        <Menu.Item
          leftSection={<IconFileTypePdf size={18} stroke={1.6} />}
          onClick={() => void run("pdf")}
        >
          <Text fz="sm" fw={500}>
            PDF (.pdf)
          </Text>
          <Text fz={11} c="gray.6">
            Laid out to print or send
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
